import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React from "react";
import { useTranslation } from "react-i18next";
import { addAppealDailyReportFeedback } from "services/appealService";
import {
  AppealDailyReport,
  AppealDailyReportFeedbackActor,
  Roles,
} from "types";
import { feedbackIcon } from "utils/constant";
import { errorHandlerOnForm, hasRole } from "utils/helper";

type Props = {
  type?: "defendant" | "prosecutor";
  appealId: number;
  dailyReport: AppealDailyReport;
  fetchData: () => void;
};

const AppealDailyReportFeedbackSection: React.FC<Props> = ({
  type = "prosecutor",
  appealId,
  dailyReport,
  fetchData,
}) => {
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();

  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);

  const [newComment, setNewComment] = React.useState("");

  return (
    <div className="ml-6">
      <h1 className="text-black font-semibold text-base flex flex-row gap-2">
        {feedbackIcon} {t("daily_report_feedback_section")}
      </h1>
      <div className="flex flex-col gap-4 mt-4 justify-between h-[70vh] w-full">
        <div className="flex flex-col overflow-y-auto gap-2">
          <div className="flex flex-col gap-4 mt-4 justify-between h-[70vh] w-full">
            {dailyReport?.coordinator_comments?.length === 0 && (
              <p className="text-gray-500 text-sm">
                {t("no_feedbacks_available")}
              </p>
            )}
            <div className="flex flex-col gap-0 w-full">
              {dailyReport?.coordinator_comments?.map((feedback, index) => (
                <div
                  key={index}
                  className={`${
                    index % 2 === 0 ? "bg-white" : "bg-gray-100"
                  } px-6 py-4 rounded-md shadow-sm mb-4`}
                  style={{
                    overflowWrap: "break-word",
                  }}
                >
                  <div className="flex justify-between items-center">
                    <p className="text-xs font-medium text-red-600">
                      {t("commented_date")}
                    </p>
                    <p className="text-xs text-gray-700">
                      {feedback?.created_at &&
                        new Date(feedback?.created_at).toLocaleString()}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 mt-2">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-200 text-gray-600 text-xs font-bold">
                      {feedback?.role === "coordinator" &&
                        t("role_coordinator_short")}
                      {feedback?.role === "director" &&
                        t("role_director_short")}
                      {feedback?.role === "deputy" && t("role_deputy_short")}
                      {feedback?.role === "attorny_general" &&
                        t("role_attorney_general_short")}
                    </span>
                    <span className="text-xs text-gray-700">
                      <span className="font-semibold text-gray-900">
                        {t("by")}:
                      </span>{" "}
                      {feedback?.role &&
                        AppealDailyReportFeedbackActor[feedback?.role]}
                    </span>
                  </div>

                  <div className="mt-3 flex flex-col gap-2">
                    <p className="text-sm text-gray-800 whitespace-pre-line">
                      {feedback?.comment}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {isCoordinator && (
              <div
                style={{
                  boxShadow: "rgb(217 217 217 / 90%) 0px -6px 7px 0px",
                }}
                className="px-6 py-4 w-full bg-white rounded-lg mt-4"
              >
                <p className="mb-2 font-semibold text-gray-800">
                  {t("write_feedback")}
                </p>
                <div className="flex flex-col sm:flex-row gap-2 items-stretch">
                  <textarea
                    rows={3}
                    className="flex-1 bg-[#F2F2F2] px-3 py-2 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#212121] text-sm"
                    placeholder={t("write_your_feedback_placeholder")}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                  />
                </div>
                <div className="mt-3 flex flex-row gap-2 justify-end">
                  <button
                    className="bg-[#212121] text-white text-sm px-6 py-2 rounded-md font-semibold hover:bg-gray-800 transition min-w-[100px]"
                    onClick={() => {
                      if (!newComment.trim()) {
                        showTooltip(t("please_enter_comment"), "error");
                        return;
                      }

                      addAppealDailyReportFeedback({
                        role: "coordinator",
                        comment: newComment.trim(),
                        appeal_request:
                          type === "defendant" ? undefined : appealId,
                        defendant_appeal_request:
                          type === "defendant" ? appealId : undefined,
                        daily_report: dailyReport?.id,
                      })
                        .then(() => {
                          setNewComment("");
                          fetchData();
                          showTooltip(
                            t("comment_added_successfully"),
                            "success",
                          );
                        })
                        .catch((error) => {
                          showTooltip(
                            errorHandlerOnForm(error) ||
                              t("error_adding_comment"),
                            "error",
                          );
                        });
                    }}
                  >
                    {t("send")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppealDailyReportFeedbackSection;
